/**
 * /c/:slug — public campaign landing page.
 *
 * Renders a single campaign with a hero image, progress thermometer,
 * full description, donate CTA, and a social-share toolbar. The share
 * URLs point to /api/og/campaigns/{slug} (server-rendered HTML with
 * proper OG tags + 1200×630 PNG), so Facebook/X/SMS previews work.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Target, Clock, Users, Share2, Copy, Check, Mail, Facebook, MessageCircle, Twitter, Megaphone } from "lucide-react";
import api, { API } from "../lib/api";
import SEO from "../components/SEO";
import { SITE_URL } from "../lib/publicSite";

const CampaignPage = () => {
    const { slug } = useParams();
    const [campaign, setCampaign] = useState(null);
    const [updates, setUpdates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setNotFound(false);
        api.get("/campaigns/public")
            .then((r) => {
                if (!alive) return;
                const match = (r.data || []).find((c) => (c.slug || "").toLowerCase() === (slug || "").toLowerCase());
                if (match) setCampaign(match);
                else setNotFound(true);
            })
            .catch(() => alive && setNotFound(true))
            .finally(() => alive && setLoading(false));
        // Updates feed — fetched separately so a campaign without updates
        // still renders the rest of the page immediately.
        api.get(`/campaigns/${slug}/updates`)
            .then((r) => alive && setUpdates(r.data || []))
            .catch(() => {});
        return () => { alive = false; };
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]" data-testid="campaign-page-loading">
                <p className="text-[#4A5568]">Loading campaign…</p>
            </div>
        );
    }

    if (notFound || !campaign) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-4 py-20" data-testid="campaign-page-not-found">
                <SEO title="Campaign not found" description="This campaign isn't active or has ended." path={`/c/${encodeURIComponent(slug)}`} noIndex />
                <div className="max-w-md text-center">
                    <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">Hmm</p>
                    <h1 className="font-heading text-4xl text-[#1C252E] font-medium mb-3">No campaign at <code className="font-mono text-2xl">/c/{slug}</code></h1>
                    <p className="text-base text-[#4A5568] leading-relaxed mb-6">
                        Either the slug is wrong, the campaign isn&apos;t active yet, or the admin hasn&apos;t created it. Try the Donate page to see what&apos;s currently raising funds.
                    </p>
                    <Link to="/donate" data-testid="campaign-not-found-donate-link" className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-6 py-3 text-sm font-medium">
                        See active campaigns <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                    </Link>
                </div>
            </div>
        );
    }

    const goal = Number(campaign.goal_amount || 0);
    const raised = Number(campaign.raised || 0);
    const hasGoal = goal > 0;
    const pct = hasGoal ? Math.min((raised / goal) * 100, 100) : 0;
    const donateLink = `/donate?campaign=${campaign.id}`;

    // Server-rendered OG-tagged URL (Facebook/X/SMS crawlers see proper preview)
    const shareUrl = `${SITE_URL}/api/og/campaigns/${encodeURIComponent(campaign.slug)}`;
    const shareText = hasGoal
        ? `${campaign.name} — $${Math.round(raised).toLocaleString()} of $${Math.round(goal).toLocaleString()} raised. Help us hit our goal!`
        : `${campaign.name} — every gift helps approved needs at Reddog Foundation.`;

    const onShareNative = () => {
        if (navigator.share) {
            navigator.share({ title: campaign.name, text: shareText, url: shareUrl }).catch(() => {});
        }
    };
    const onCopy = async () => {
        try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); }
        catch { window.prompt("Copy this link", shareUrl); }
    };
    const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    const tw = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    const mail = `mailto:?subject=${encodeURIComponent(campaign.name)}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`;
    const sms = `sms:?&body=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;

    return (
        <div className="bg-[#FDFBF7] min-h-screen" data-testid={`campaign-page-${campaign.slug}`}>
            <SEO
                title={`${campaign.name} · Reddog Foundation`}
                description={campaign.description || `Help us reach our ${hasGoal ? `$${goal.toLocaleString()} ` : ""}goal — every gift counts.`}
                path={`/c/${campaign.slug}`}
                image={campaign.image_url}
            />

            {/* Hero image — uses object-contain so tall poster-style campaign
                images aren't cropped. Letterbox bars use the cream brand color. */}
            {campaign.image_url && (
                <div className="w-full bg-[#F4F1EA]" data-testid="campaign-hero-image">
                    <div className="max-w-5xl mx-auto">
                        <img
                            src={campaign.image_url}
                            alt={campaign.name}
                            className="w-full max-h-[70vh] object-contain"
                        />
                    </div>
                </div>
            )}

            <div className="max-w-3xl mx-auto px-6 sm:px-8 py-12 lg:py-16 [overflow-wrap:anywhere]">
                <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3 flex items-center gap-2">
                    <Target className="w-3.5 h-3.5" strokeWidth={2} /> Active campaign
                </p>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#1C252E] font-medium tracking-tight leading-[1.05] mb-6">
                    {campaign.name}
                </h1>

                {campaign.description && (
                    <p className="text-lg text-[#4A5568] leading-relaxed mb-10 max-w-2xl whitespace-pre-wrap">
                        {campaign.description}
                    </p>
                )}

                {/* Thermometer */}
                <div className="bg-white border border-black/5 rounded-3xl p-6 sm:p-8 mb-10" data-testid="campaign-progress">
                    {hasGoal ? (
                        <>
                            <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                                <p className="font-heading text-3xl md:text-4xl text-[#1C252E] font-medium">
                                    ${Math.round(raised).toLocaleString()}
                                    <span className="text-base text-[#4A5568] font-normal"> of ${Math.round(goal).toLocaleString()}</span>
                                </p>
                                <p className="text-base font-bold text-[#E53935]" data-testid="campaign-pct">{pct.toFixed(0)}%</p>
                            </div>
                            <div className="h-4 w-full bg-[#F4F1EA] rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-[#E53935] to-[#D32F2F] rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
                            </div>
                        </>
                    ) : (
                        <p className="font-heading text-3xl md:text-4xl text-[#1C252E] font-medium">
                            ${Math.round(raised).toLocaleString()}{" "}
                            <span className="text-base text-[#4A5568] font-normal">raised — flexible fund</span>
                        </p>
                    )}
                    <div className="flex items-center gap-5 mt-4 text-sm text-[#4A5568] flex-wrap">
                        <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" strokeWidth={1.5} /> {campaign.donors} {campaign.donors === 1 ? "donor" : "donors"}</span>
                        {campaign.days_left !== null && campaign.days_left !== undefined && (
                            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> {campaign.days_left === 0 ? "Ends today" : `${campaign.days_left} day${campaign.days_left === 1 ? "" : "s"} left`}</span>
                        )}
                    </div>
                </div>

                {/* CTA */}
                <Link
                    to={donateLink}
                    data-testid="campaign-donate-cta"
                    className="inline-flex items-center justify-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-8 py-4 text-base font-medium transition-all hover:-translate-y-0.5 mb-12"
                >
                    Give to this campaign <ArrowRight className="w-5 h-5" strokeWidth={2} />
                </Link>

                {/* Updates timeline — populated by admin posts. Each update is
                    a milestone the donor can re-share. Newest first. */}
                {updates.length > 0 && (
                    <div className="mb-12" data-testid="campaign-updates-feed">
                        <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-4 flex items-center gap-2">
                            <Megaphone className="w-3.5 h-3.5" strokeWidth={2} /> Updates from the foundation
                        </p>
                        <ol className="space-y-6 relative border-l-2 border-[#F2D8D5] pl-6">
                            {updates.map((u) => {
                                const when = new Date(u.created_at);
                                return (
                                    <li key={u.id} className="relative" data-testid={`campaign-update-${u.id}`}>
                                        <span className="absolute -left-[31px] top-1 w-3 h-3 rounded-full bg-[#E53935] ring-4 ring-[#FDFBF7]" />
                                        <p className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568] mb-1">
                                            {when.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} · {u.author_name}
                                        </p>
                                        <h3 className="font-heading text-xl text-[#1C252E] font-medium mb-2 leading-snug">{u.title}</h3>
                                        {u.image_url && (
                                            <img src={u.image_url} alt={u.title} loading="lazy" className="w-full max-h-72 object-contain rounded-2xl bg-[#F4F1EA] mb-3" />
                                        )}
                                        <p className="text-sm text-[#4A5568] leading-relaxed whitespace-pre-wrap">{u.body}</p>
                                    </li>
                                );
                            })}
                        </ol>
                    </div>
                )}

                {/* Share toolbar */}
                <div className="min-w-0 max-w-full bg-white border border-black/5 rounded-3xl p-6 sm:p-8" data-testid="campaign-share-toolbar">
                    <div className="flex items-start gap-3 mb-5">
                        <Share2 className="w-5 h-5 text-[#E53935] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div className="min-w-0">
                            <p className="font-heading text-xl text-[#1C252E] font-medium">Share with friends</p>
                            <p className="text-sm text-[#4A5568] mt-1">Every share helps. Posts include the campaign image and progress automatically.</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <a href={fb} target="_blank" rel="noopener noreferrer" data-testid="campaign-share-facebook" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium bg-[#1877F2] text-white hover:opacity-90">
                            <Facebook className="w-4 h-4" strokeWidth={1.5} /> Facebook
                        </a>
                        <a href={tw} target="_blank" rel="noopener noreferrer" data-testid="campaign-share-twitter" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium bg-[#1C252E] text-white hover:bg-black">
                            <Twitter className="w-4 h-4" strokeWidth={1.5} /> X / Twitter
                        </a>
                        <a href={mail} data-testid="campaign-share-email" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium bg-white border border-black/10 text-[#1C252E] hover:border-[#E53935]">
                            <Mail className="w-4 h-4" strokeWidth={1.5} /> Email
                        </a>
                        <a href={sms} data-testid="campaign-share-sms" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium bg-white border border-black/10 text-[#1C252E] hover:border-[#E53935] sm:hidden">
                            <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Text
                        </a>
                        <button
                            type="button"
                            onClick={onCopy}
                            data-testid="campaign-share-copy"
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium bg-white border border-black/10 text-[#1C252E] hover:border-[#E53935]"
                        >
                            {copied ? <><Check className="w-4 h-4 text-green-700" strokeWidth={2.5} /> Copied!</> : <><Copy className="w-4 h-4" strokeWidth={1.5} /> Copy link</>}
                        </button>
                        {typeof navigator !== "undefined" && navigator.share && (
                            <button
                                type="button"
                                onClick={onShareNative}
                                data-testid="campaign-share-native"
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium bg-[#E53935] text-white hover:bg-[#D32F2F]"
                            >
                                <Share2 className="w-4 h-4" strokeWidth={1.5} /> More…
                            </button>
                        )}
                    </div>
                    <p data-testid="campaign-share-url" className="text-xs text-[#4A5568] mt-4 min-w-0">
                        Share link: <code className="text-xs bg-[#F4F1EA] px-2 py-0.5 rounded whitespace-normal break-all">{shareUrl}</code>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CampaignPage;
